import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Activity, Heart } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './BmiCalculatorPage/components/CalculationForm';
import ResultsOverview from './BmiCalculatorPage/components/ResultsOverview';
import BmiChart from './BmiCalculatorPage/components/BmiChart';
import QuickAdjustmentSidebar from './BmiCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './BmiCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './BmiCalculatorPage/components/TipsSidebar';
import InfoContent from './BmiCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  weight: '70',
  height: '170',
  age: '30',
  gender: 'male', // 'male' или 'female'
  activityLevel: 'moderate', // 'sedentary', 'light', 'moderate', 'active', 'very_active'
  measurementSystem: 'metric', // 'metric' или 'imperial'
  bodyType: 'normal', // 'asthenic', 'normal', 'hypersthenic'
  goal: 'maintain', // 'lose', 'maintain', 'gain'
};

// Определение уровней активности
export const activityLevels = [
  { id: 'sedentary', name: 'Малоподвижный', description: 'Сидячая работа, минимум физической активности', factor: 1.2 },
  { id: 'light', name: 'Низкая активность', description: 'Легкие тренировки 1-3 раза в неделю', factor: 1.375 },
  { id: 'moderate', name: 'Умеренная активность', description: 'Тренировки 3-5 раз в неделю', factor: 1.55 },
  { id: 'active', name: 'Высокая активность', description: 'Интенсивные тренировки 6-7 раз в неделю', factor: 1.725 },
  { id: 'very_active', name: 'Очень высокая активность', description: 'Тяжелая физическая работа или 2 тренировки в день', factor: 1.9 }
];

// Определение типов телосложения
export const bodyTypes = [
  { id: 'asthenic', name: 'Астенический (эктоморф)', description: 'Тонкокостный, худощавый, с трудом набирает вес', factor: 0.9 },
  { id: 'normal', name: 'Нормостенический (мезоморф)', description: 'Средняя комплекция, атлетическое телосложение', factor: 1.0 },
  { id: 'hypersthenic', name: 'Гиперстенический (эндоморф)', description: 'Ширококостный, легко набирает вес', factor: 1.1 }
];

// Определение целей
export const weightGoals = [
  { id: 'lose', name: 'Снижение веса', description: 'Сжигание жира и уменьшение массы тела', factor: 0.85 },
  { id: 'maintain', name: 'Поддержание веса', description: 'Сохранение текущей массы тела', factor: 1.0 },
  { id: 'gain', name: 'Набор веса', description: 'Увеличение мышечной массы', factor: 1.15 }
];

/**
 * BmiCalculatorPage Component - Полный калькулятор индекса массы тела
 */
const BmiCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('bmi-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор ИМТ (индекса массы тела) | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте свой индекс массы тела (ИМТ) с учетом возраста, пола, типа телосложения и получите персональные рекомендации по здоровому весу.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedBmiCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved BMI calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'for-adults':
          newState.age = '30';
          break;
        case 'for-children':
          newState.age = '12';
          break;
        case 'for-elderly':
          newState.age = '65';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `ИМТ ${formData.height}см, ${formData.weight}кг`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedBmiCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedBmiCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Heart className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор ИМТ (индекса массы тела)'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              activityLevels={activityLevels}
              bodyTypes={bodyTypes}
              weightGoals={weightGoals}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <BmiChart 
                  bmi={results.bmi} 
                  category={results.category}
                  weight={parseFloat(formData.weight)}
                  height={parseFloat(formData.height)}
                  goalWeight={results.idealWeight}
                />
              </>
            )}

            {/* --- Информационный контент об индексе массы тела --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default BmiCalculatorPage;